'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
});

interface GoogleMapsProviderProps {
  children: React.ReactNode;
}

export const useGoogleMaps = () => useContext(GoogleMapsContext);

// Helper function to detect desktop synchronously
const getIsDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 768;
};

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isDesktop] = useState(() => getIsDesktop()); // Detect desktop once on mount

  // Start loading Google Maps for all devices (some pages need maps on mobile too)
  // Desktop gets priority with requestIdleCallback, mobile loads immediately
  useEffect(() => {
    if (isDesktop) {
      // Use requestIdleCallback for better performance on desktop
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => setShouldLoad(true), { timeout: 1000 });
      } else {
        const timer = setTimeout(() => setShouldLoad(true), 100);
        return () => clearTimeout(timer);
      }
    } else {
      // Load immediately on mobile for pages that need it
      const timer = setTimeout(() => setShouldLoad(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isDesktop]);

  // Use stable configuration - provide API key for all devices (needed for pages that show maps on mobile too)
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: undefined,
    preventGoogleFontsLoading: true,
    id: 'google-maps-script'
  });

  // Log Google Maps loading status and errors
  useEffect(() => {
    if (!shouldLoad) return;
    
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      console.error('[GoogleMaps] CRITICAL: NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is not set or is empty');
      return;
    }

    if (loadError) {
      console.error('[GoogleMaps] Failed to load Google Maps script:', {
        error: loadError.message,
        stack: loadError.stack,
        apiKeyPresent: !!apiKey,
        apiKeyLength: apiKey.length,
        timestamp: new Date().toISOString()
      });
      
      // Log additional debugging info
      if (typeof window !== 'undefined') {
        console.error('[GoogleMaps] Window details:', {
          userAgent: window.navigator.userAgent,
          url: window.location.href,
          referrer: document.referrer
        });
      }
    } else if (isLoaded) {
      console.log('[GoogleMaps] Successfully loaded Google Maps script:', {
        apiKeyPresent: !!apiKey,
        timestamp: new Date().toISOString()
      });
    }
  }, [isLoaded, loadError, shouldLoad]);

  return (
    <GoogleMapsContext.Provider value={{ 
      isLoaded: shouldLoad && isLoaded, 
      loadError: shouldLoad ? loadError : undefined 
    }}>
      {children}
    </GoogleMapsContext.Provider>
  );
}; 